import { ChangeDetectionStrategy, Component, ElementRef, EventEmitter, OnInit, ViewChild } from '@angular/core';
import { embedDashboard } from '@superset-ui/embedded-sdk/bundle';
import { SessionDetailsPatientTimelineService } from './session-details-patient-timeline.service';
import { environment } from '@env';
import { NotificationsService } from '@lib/notifications';
import { TranslateService } from '@project/translate';
import { Observable } from 'rxjs';

@Component({
  selector: 'app-session-details-patient-timeline',
  templateUrl: './session-details-patient-timeline.component.html',
  styleUrls: ['./session-details-patient-timeline.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SessionDetailsPatientTimeLineComponent implements OnInit {
  @ViewChild('supersetContainer', { static: true }) supersetContainer: ElementRef;

  public close$ = new EventEmitter<void>();
  public submit$ = new EventEmitter<void>();

  public patientCpf$: Observable<string>;

  private readonly supersetDomain = environment.environmentVariables.supersetDomain;
  private readonly supersetDashboardID = environment.environmentVariables.supersetDashboardID;

  constructor(
    private sessionDetailsPatientTimelineService: SessionDetailsPatientTimelineService,
    private notificationService: NotificationsService,
  ) {}

  ngOnInit() {
    this.patientCpf$.subscribe({
      next: (patientCpf: string) => {
        if (patientCpf) {
          this.embedSupersetDashboard(patientCpf)
            .then(() => {
              setTimeout(() => this.refreshIframe(patientCpf), 1000);
            })
            .catch((error) => this.handleEmbedError(error));
        }
      },
      error: (error) => this.handleEmbedError(error),
    });
  }

  private refreshIframe(patientCpf: string): void {
    if (this.supersetContainer?.nativeElement) {
      this.supersetContainer.nativeElement.innerHTML = '';
      this.embedSupersetDashboard(patientCpf).catch((error) => this.handleEmbedError(error));
    }
  }

  private async embedSupersetDashboard(patientCpf: string): Promise<void> {
    try {
      await embedDashboard({
        id: this.supersetDashboardID,
        supersetDomain: this.supersetDomain,
        mountPoint: this.supersetContainer.nativeElement,
        fetchGuestToken: () => this.sessionDetailsPatientTimelineService.fetchTokenFromBackend(),
        dashboardUiConfig: {
          hideTitle: true,
          urlParams: {
            standalone: 2,
            patient_cpf_str: patientCpf,
          },
        },
      });
      this.applyContainerStyles();
    } catch (error) {
      this.handleEmbedError(error);
    }
  }

  private applyContainerStyles(): void {
    const container = this.supersetContainer.nativeElement as HTMLElement;
    const childElement = container.firstElementChild as HTMLElement;

    if (childElement) {
      childElement.style.width = '100%';
      childElement.style.height = '100%';
    }
  }

  private handleEmbedError(error: unknown): void {
    const message =
      error instanceof Error ? error.message : TranslateService.localize('errors.superset-dashboard-embed.error');

    this.notificationService.error({ message });
  }

  close(): void {
    this.close$.emit();
  }
}
